import { buildMarginal } from './xirrFee';
import { applyRepaymentCapacity } from './cohortRepayments';
import {
  resolveCohortDraw,
  resolveEffectiveRevSharePct,
} from './resolvePlanFunding';

export function resolveLastMarginalDelta(cumulativeRoasPct) {
  if (!cumulativeRoasPct?.length) {
    return 0;
  }
  if (cumulativeRoasPct.length === 1) {
    return Math.max(0, Number(cumulativeRoasPct[0]) || 0);
  }
  const last = Number(cumulativeRoasPct[cumulativeRoasPct.length - 1]) || 0;
  const prev = Number(cumulativeRoasPct[cumulativeRoasPct.length - 2]) || 0;
  return Math.max(0, last - prev);
}

export function extendCumulativeRoasByLastMarginal(cumulativeRoasPct, targetPeriods) {
  const roas = [...(cumulativeRoasPct || [])];
  const periods = Math.max(1, Math.floor(Number(targetPeriods) || roas.length));
  if (periods <= roas.length) {
    return roas.slice(0, periods);
  }
  const delta = resolveLastMarginalDelta(roas);
  while (roas.length < periods) {
    roas.push(roas[roas.length - 1] + delta);
  }
  return roas;
}

function simulateCohortRepaymentOutstanding({
  cumulativeRoasPct,
  drawAmount,
  marketing,
  revSharePct,
  gracePeriod,
  monthlyFeePct,
  maxRepaymentPeriods,
  skipFinalForcedPayoff = true,
}) {
  const roas = [...(cumulativeRoasPct || [])];
  const periods = Math.max(1, maxRepaymentPeriods ?? roas.length);
  while (roas.length < periods) {
    roas.push(roas.length > 0 ? roas[roas.length - 1] : 0);
  }
  const trimmed = roas.slice(0, periods);
  const marginal = buildMarginal(trimmed);
  const drawMonth = 0;
  const finalMonth = drawMonth + gracePeriod + trimmed.length - 1;

  let outstandingPrincipal = drawAmount;
  let outstandingFee = 0;

  for (let month = drawMonth; month <= finalMonth; month += 1) {
    const monthlyFeeAccrual =
      month > drawMonth && outstandingPrincipal + outstandingFee > 0 && monthlyFeePct > 0
        ? Math.round(
            ((outstandingPrincipal + outstandingFee) * monthlyFeePct) / 100
          )
        : 0;
    outstandingFee += monthlyFeeAccrual;

    let capacity = 0;
    if (month >= drawMonth + gracePeriod) {
      const k = month - drawMonth - gracePeriod;
      if (k >= 0 && k < trimmed.length) {
        const cohortRevenue = (marketing * (marginal[k] ?? 0)) / 100;
        capacity = Math.round((cohortRevenue * revSharePct) / 100);
      }
    }

    ({ outstandingPrincipal, outstandingFee } = applyRepaymentCapacity({
      month,
      capacity,
      outstandingPrincipal,
      outstandingFee,
      repaymentsByMonth: {},
      principalRepaymentsByMonth: {},
      feeRepaymentsByMonth: {},
    }));

    if (outstandingPrincipal <= 0 && outstandingFee <= 0) {
      return {
        outstandingPrincipal: 0,
        outstandingFee: 0,
        repaidEarly: month < finalMonth,
      };
    }
  }

  if (!skipFinalForcedPayoff) {
    return { outstandingPrincipal: 0, outstandingFee: 0, repaidEarly: false };
  }

  return {
    outstandingPrincipal,
    outstandingFee,
    repaidEarly: false,
  };
}

export function isFullyRepaidWithinRawVector(cumulativeRoasPct, cohortContext) {
  const raw = [...(cumulativeRoasPct || [])];
  if (!raw.length || cohortContext.drawAmount <= 0) {
    return true;
  }
  const result = simulateCohortRepaymentOutstanding({
    cumulativeRoasPct: raw,
    maxRepaymentPeriods: raw.length,
    ...cohortContext,
  });
  return result.outstandingPrincipal <= 0 && result.outstandingFee <= 0;
}

export function topUpFinalMarginalForRepayment(cumulativeRoasPct, cohortContext) {
  const {
    drawAmount,
    marketing,
    revSharePct,
    gracePeriod,
    monthlyFeePct,
    maxRepaymentPeriods,
  } = cohortContext;

  if (drawAmount <= 0 || !marketing || !maxRepaymentPeriods) {
    return [...(cumulativeRoasPct || [])];
  }

  const roas = [...(cumulativeRoasPct || [])];
  const lastIndex = roas.length - 1;
  if (lastIndex < 0) {
    return roas;
  }

  const outstanding = simulateCohortRepaymentOutstanding({
    cumulativeRoasPct: roas,
    drawAmount,
    marketing,
    revSharePct,
    gracePeriod,
    monthlyFeePct,
    maxRepaymentPeriods,
    skipFinalForcedPayoff: true,
  });

  const totalOutstanding =
    outstanding.outstandingPrincipal + outstanding.outstandingFee;
  if (totalOutstanding <= 0) {
    return roas;
  }

  const revShareFactor = Math.max(0, Number(revSharePct) || 0) / 100;
  if (revShareFactor <= 0) {
    return roas;
  }

  const neededCohortRevenue = totalOutstanding / revShareFactor;
  const neededMarginal = (neededCohortRevenue * 100) / marketing;
  const prevCumulative = lastIndex > 0 ? roas[lastIndex - 1] : 0;
  roas[lastIndex] = Math.max(roas[lastIndex], prevCumulative + neededMarginal);
  return roas;
}

/**
 * Resolve the cumulative ROAS vector used for cohort revenue and repayments.
 * Extension is calc-time only; stored ROAS / Hill output is unchanged.
 */
export function resolveRepaymentRoasVector(
  cumulativeRoasPct,
  maxRepaymentPeriods,
  cohortContext = null
) {
  const raw = [...(cumulativeRoasPct || [])];
  const targetPeriods = Math.max(
    1,
    Math.floor(Number(maxRepaymentPeriods) || raw.length)
  );

  if (!cohortContext || cohortContext.drawAmount <= 0) {
    return raw;
  }

  if (isFullyRepaidWithinRawVector(raw, cohortContext)) {
    return raw;
  }

  if (targetPeriods <= raw.length) {
    return raw.slice(0, targetPeriods);
  }

  const lastCumulative = raw.length ? Number(raw[raw.length - 1]) || 0 : 0;
  if (lastCumulative >= 100) {
    return raw;
  }

  let extended = extendCumulativeRoasByLastMarginal(raw, targetPeriods);
  extended = topUpFinalMarginalForRepayment(extended, {
    ...cohortContext,
    maxRepaymentPeriods: targetPeriods,
  });
  return extended;
}

export function resolveAllCohortRepaymentRoasVectors(
  state,
  numCohorts,
  spendRoasVectors,
  targetPeriods,
  monthlyFeePct,
  gracePeriod
) {
  const grace = Math.max(0, Number(gracePeriod) || 0);
  const feePct = Number(monthlyFeePct) || 0;
  return Array.from({ length: numCohorts }, (_, i) => {
    const raw = [...(spendRoasVectors?.[i] ?? state.roas ?? [])];
    const drawAmount = resolveCohortDraw(state, i);
    return resolveRepaymentRoasVector(raw, targetPeriods, {
      drawAmount,
      marketing: Number(state.marketing[i]) || 0,
      revSharePct: resolveEffectiveRevSharePct(state, i),
      gracePeriod: grace,
      monthlyFeePct: feePct,
    });
  });
}
